import React, { useState } from 'react';
import { X, Clock, RefreshCw, Voicemail, FileText, Brain, Target, MessageCircle, TrendingUp, ListChecks, Search, UserCheck, Phone, CalendarCheck, BarChart3, Flame, Sun, Snowflake } from 'lucide-react';

interface HowIWorkModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'process' | 'behavior' | 'intelligence';

const processSteps = [
  {
    icon: Search,
    title: 'Receive & Research Leads',
    description: 'I receive your approved leads and research each prospect — their company, role, industry, and recent activity — so every call feels personal and informed.',
  },
  {
    icon: FileText,
    title: 'Generate Personalized Script',
    description: 'Using your pitch book, objection handling rules, and the lead\'s profile, I craft a tailored call script designed to resonate with each individual prospect.',
  },
  {
    icon: Clock,
    title: 'Schedule Optimal Call Time',
    description: 'I calculate the best time to call based on the prospect\'s timezone, industry norms, and historical answer-rate patterns to maximize pickup probability.',
  },
  {
    icon: Phone,
    title: 'Execute the Call',
    description: 'I place the call using your assigned phone number, follow the personalized script, handle objections in real time, and adapt the conversation dynamically.',
  },
  {
    icon: UserCheck,
    title: 'Qualify & Categorize',
    description: 'After each conversation, I classify the lead as hot, warm, or cold based on interest signals, objections raised, and engagement quality during the call.',
  },
  {
    icon: CalendarCheck,
    title: 'Book Meeting or Schedule Follow-up',
    description: 'If the prospect is interested, I book a meeting directly into your calendar. Otherwise, I schedule an intelligent follow-up at the right time and channel.',
  },
  {
    icon: BarChart3,
    title: 'Log & Analyze Results',
    description: 'Every call is logged with a structured summary — outcome, objections, sentiment, and next steps — so you have full visibility without listening to recordings.',
  },
];

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'process', label: 'Process', icon: ListChecks },
  { id: 'behavior', label: 'Behavior', icon: Target },
  { id: 'intelligence', label: 'Intelligence', icon: Brain },
];

const HowIWorkModal: React.FC<HowIWorkModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabId>('process');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-lg flex-shrink-0">
              <Brain className="text-white" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-extrabold text-gray-900 font-display tracking-tight">How I Work</h3>
              <p className="text-sm text-gray-600 mt-1">Understanding my process, automation and intelligence</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex border-b border-gray-200 bg-gray-50">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-6 py-4 font-semibold transition-all duration-300 relative ${
                  activeTab === tab.id
                    ? 'text-[#1875FF] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="flex items-center justify-center space-x-2">
                  <Icon size={20} />
                  <span>{tab.label}</span>
                </span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-t-full" />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'process' && <ProcessTab />}
          {activeTab === 'behavior' && <BehaviorTab />}
          {activeTab === 'intelligence' && <IntelligenceTab />}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-semibold hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

const ProcessTab = () => (
  <div className="space-y-4 animate-in slide-in-from-left duration-300">
    {processSteps.map((step, index) => {
      const Icon = step.icon;
      return (
        <div
          key={index}
          className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-5 hover:border-[#1875FF]/30 transition-all hover:shadow-lg"
        >
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 self-center">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-lg">
                <Icon className="text-white" size={24} />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-base font-bold text-gray-400 mb-1">
                STEP {index + 1}: <span className="text-gray-900">{step.title}</span>
              </h4>
              <p className="text-gray-700 leading-relaxed">{step.description}</p>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const BehaviorTab = () => (
  <div className="space-y-6 animate-in slide-in-from-left duration-300">
    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Clock className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Calling Hours</h4>
          <p className="text-gray-700 leading-relaxed">
            I operate during business hours (9 AM - 6 PM) in the prospect's local timezone.
            I automatically adjust my calling schedule based on time zones to ensure I never
            disturb prospects outside appropriate hours.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <RefreshCw className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Retry Rules</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            When a lead doesn't answer, I intelligently schedule follow-ups:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">•</span>
              <span>First retry: 2-4 hours later, different time window</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">•</span>
              <span>Second retry: Next business day, morning slot</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">•</span>
              <span>Third retry: 2 days later, afternoon slot</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">•</span>
              <span>Maximum attempts: 5 calls before marking as unreachable</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Voicemail className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Voicemail Logic</h4>
          <p className="text-gray-700 leading-relaxed">
            I detect voicemail systems and leave personalized messages on the 2nd and 4th attempt.
            Each voicemail is natural, professional, and includes a clear value proposition with
            callback information.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Target className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Qualification Criteria</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I qualify leads based on these key factors:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#05E0D1] mr-2">&#10003;</span>
              <span>Budget availability and timeline</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#05E0D1] mr-2">&#10003;</span>
              <span>Decision-making authority</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#05E0D1] mr-2">&#10003;</span>
              <span>Current pain points matching our solution</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#05E0D1] mr-2">&#10003;</span>
              <span>Interest level and engagement quality</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <FileText className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">When I Stop Calling</h4>
          <p className="text-gray-700 leading-relaxed">
            I stop calling when: the lead explicitly requests no further contact, 5 unsuccessful
            attempts have been made, the lead is marked as not qualified, or a meeting has been
            successfully booked.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const IntelligenceTab = () => (
  <div className="space-y-6 animate-in slide-in-from-right duration-300">
    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <MessageCircle className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Objection Detection</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I use advanced natural language processing to detect and categorize objections:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Price objections:</strong> Budget concerns, cost comparisons</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Timing objections:</strong> "Call back later", "Not right now"</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Authority objections:</strong> "Need to check with team"</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Need objections:</strong> "Already have a solution"</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <TrendingUp className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Temperature Classification</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I classify leads into temperature categories based on engagement:
          </p>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-red-500/10 to-orange-500/10 text-red-600 border border-red-400/20 shadow-sm shadow-red-500/10">
              <Flame size={14} className="text-red-500" />
              Hot Lead
            </span>
            <span className="text-gray-300 font-light">|</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-500/8 text-amber-600 border border-amber-500/15 shadow-sm">
              <Sun size={14} />
              Warm Lead
            </span>
            <span className="text-gray-300 font-light">|</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#1875FF]/8 text-[#1875FF] border border-[#1875FF]/15 shadow-sm">
              <Snowflake size={14} />
              Cold Lead
            </span>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <FileText className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Conversation Summarization</h4>
          <p className="text-gray-700 leading-relaxed">
            After each call, I analyze the conversation and create structured summaries extracting
            key information like objections, interest level, pain points discussed, next steps agreed
            upon, and overall sentiment. This makes it easy for you to review and take action.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Brain className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Pattern Learning</h4>
          <p className="text-gray-700 leading-relaxed">
            I continuously learn from successful conversations to improve my approach. I identify
            which talking points resonate best, optimal calling times, effective objection handling
            techniques, and conversation patterns that lead to bookings.
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default HowIWorkModal;
